import { ChiptaElement, html, css } from 'chipta-libs/main.js';
import { apiGet } from 'chipta-libs/api/utils.js';
import 'chipta-libs/components/styled-input.js';
import 'chipta-libs/components/outside-click.js';
import 'chipta-libs/components/font-awesome.js';
import { filterOnLocation } from './state/events.js';


class EventsGeoFilter extends ChiptaElement {

  constructor() {
    super();
    this.searchTerm = null;
    this.timer = null;
    this.showResults = false;
    this.results = [];
    this.highlightedResult = null;
    this.lat = null;
    this.lon = null;
    this.searching = false;
    this.inputJustGotFocus = false;
  }

  static get properties() {
    return {
      showResults: {type: Boolean},
      searching: {type: Boolean},
      results: {type: Array},
      highlightedResult: {type: Number}
    };
  }

  render() {

    return html`

      <div id="inputs">

        <outside-click @outside-click=${this.outsideClick}>

          <styled-input id="location-input-container">
            <input
              id="location-input"
              type="text"
              placeholder=${gettext('enter_a_location')}
              @keyup=${this.locationInputKeyUp}
              @click=${this.locationInputClick}
              @focus=${this.locationInputFocus}
              @blur=${this.locationInputBlur}
            />
            <font-awesome
              id="input-clear-button"
              icon='times'
              icon-style='custom'
              @click=${this.clearSearch}
            ></font-awesome>
          </styled-input>

          <div
            id="results"
            ?hidden=${!this.showResults || !(this.results.length || this.searching)}
            @mouseenter=${this.resultsMouseEnter}
          >
            ${this.resultsList}
          </div>

        </outside-click>

        <styled-input>
          <select id="range-select" @change=${this.rangeChange}>
            ${this.rangeOptions()}
          </select>
        </styled-input>

      </div>

    `;

  }

  *rangeOptions() {
    for (let value of [10, 25, 50, 75, 100, 150]) {
      yield html`<option value=${value} ?selected=${this.selectedRange === value}>${value} km</option>`;
    }
  }

  get selectedRange() {
    return _ticketshopData.events_geo_filter_settings.default_range;
  }

  get resultsList() {

    if (!this.searchTerm) {
      return;
    }

    if (this.searching) {
      return html`<div><font-awesome icon='circle-notch'></font-awesome></div>`;
    }

    if (!this.results.length) {
      return html`<div>${gettext('no_results')}</div>`;
    }

    return this.results.map((result, index) => {
      return html`
        <div
          @click=${this.resultClick}
          data-lat=${result['lat']}
          data-lon=${result['lon']}
          ?selected=${this.highlightedResult === index}
        >
          ${result['address']}
        </div>
      `;
    });

  }

  static get styles() {

    return css`

      :host {
        display: block;
      }

      #inputs {
        display: flex;
      }

      #location-input-container {
        position: relative;
      }

      #location-input {
        padding-right: 21px;
        width: 100%;
        max-width: 400px;
        border-right: 0;
        border-radius: 3px 0 0 3px;
        text-overflow: ellipsis;
      }

      #input-clear-button {
        position: absolute;
        padding: 2px;
        top: 6px;
        right: 3px;
        font-size: 12px;
        color: #777;
        cursor: pointer;
      }

      #range-select {
        border-radius: 0 3px 3px 0;
      }

      #results {
        position: absolute;
        background: #FFF;
        min-width: 200px;
        max-width: 600px;
        border: 1px solid #CCC;
      }

      #results > div {
        padding: 5px 10px;
        border-bottom: 1px solid #CCC;
        cursor: pointer;
      }

      #results > div:hover,
      #results > div[selected] {
        background: #DDD;
      }

    `;

  }

  outsideClick(event) {
    this.showResults = false;
  }

  locationInputKeyUp(event) {
    if (event.key === 'ArrowDown') this.highlightNextResult();
    else if (event.key === 'ArrowUp') this.highlightPrevResult();
    else if (event.key === 'Enter') this.selectHighlightedResult();
    else if (event.key === 'Escape') this.showResults = false;
    else if (event.target.value !== this.searchTerm) this.updateSearchTerm(event.target.value);
  }

  clearSearch() {
    this.el('#location-input').value = '';
    this.showResults = false;
    this.resetSearch();
  }

  locationInputClick(event) {
    if (!this.inputJustGotFocus) {
      this.showResults = !this.showResults;
    }
  }

  locationInputFocus() {

    this.showResults = true;
    this.inputJustGotFocus = true;

    setTimeout(() => {
      this.inputJustGotFocus = false;
    }, 100);

  }

  locationInputBlur() {
    // wait 100 ms to allow for possible click events on the results to finish
    setTimeout(() => {
      this.showResults = false;
    }, 100);
  }

  resultsMouseEnter() {
    this.highlightedResult = null;
  }

  resultClick(event) {
    this.selectResult(event.target);
  }

  highlightNextResult() {

    this.showResults = true;

    if (this.highlightedResult === null) {
      this.highlightedResult = 0;
    } else {
      this.highlightedResult += 1;
    }

    if (this.highlightedResult >= this.results.length) {
      this.highlightedResult = 0;
    }

  }

  highlightPrevResult() {

    this.showResults = true;

    if (this.highlightedResult === null) {
      this.highlightedResult = this.results.length - 1;
    } else {
      this.highlightedResult -= 1;
    }

    if (this.highlightedResult < 0) {
      this.highlightedResult = this.results.length - 1;
    }

  }

  selectHighlightedResult() {

    if (this.highlightedResult === null) {
      return;
    }

    this.selectResult(this.els('#results > div')[this.highlightedResult]);

  }

  updateSearchTerm(searchTerm) {

    this.searchTerm = searchTerm;
    this.results = [];
    this.showResults = Boolean(searchTerm);
    this.searching = Boolean(searchTerm);
    this.highlightedResult = null;

    if (this.timer) {
      clearTimeout(this.timer);
    }

    this.timer = setTimeout(() => {

      if (!this.searchTerm) {
        this.resetSearch();
        return;
      }

      this.search(this.searchTerm);

    }, 1000);

  }

  resetSearch() {
    this.lat = null;
    this.lon = null;
    this.filterEvents();
  }

  search(searchTerm) {
    apiGet(`geosearch/?search=${searchTerm}`).then(response => {
      this.results = response.data;
    }).catch(response => {
      this.results = [];
    }).finally(() => {
      this.searching = false;
    });
  }

  selectResult(resultEl) {
    this.el('#location-input').value = resultEl.innerText;
    this.showResults = false;
    this.lat = resultEl.dataset.lat;
    this.lon = resultEl.dataset.lon;
    this.filterEvents();
  }

  filterEvents() {

    const rangeEl = this.el('#range-select');

    filterOnLocation(
      this.lat,
      this.lon,
      rangeEl.options[rangeEl.selectedIndex].value
    );

  }

}

customElements.define('events-geo-filter', EventsGeoFilter);
